import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Combobox d'autocomplete pour sélectionner un ingrédient de la BD.
 *
 * Props :
 * - value: string (nom courant de l'ingrédient dans la recette)
 * - onChange: (name, ingredientObj?) => void — appelé à chaque frappe. Si l'utilisateur sélectionne
 *   un ingrédient existant (via menu déroulant), `ingredientObj` est passé pour auto-remplissage.
 * - ingredients: array — liste complète depuis /api/ingredients (déjà triée alphabétique)
 * - onCreate: (newIngredient) => void — appelé après création inline ; reçoit l'objet ingrédient
 * - placeholder: string
 * - className: string
 * - testid: string
 */
export default function IngredientCombobox({
  value,
  onChange,
  ingredients = [],
  onCreate,
  placeholder = "Tapez ou choisissez…",
  className = "",
  testid,
}) {
  const [focused, setFocused] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [creating, setCreating] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const trimmed = (value || "").trim();
  const lower = trimmed.toLowerCase();

  // Filtre par préfixe d'abord, puis contient (pour pertinence)
  const filtered = useMemo(() => {
    if (!focused) return [];
    if (!lower) return ingredients.slice(0, 30);
    const starts = [];
    const contains = [];
    for (const it of ingredients) {
      const n = (it.name || "").toLowerCase();
      if (n.startsWith(lower)) starts.push(it);
      else if (n.includes(lower)) contains.push(it);
    }
    return [...starts, ...contains].slice(0, 30);
  }, [ingredients, lower, focused]);

  // Y a-t-il un match exact (insensible casse) ?
  const exactMatch = useMemo(
    () => ingredients.find((it) => (it.name || "").toLowerCase() === lower),
    [ingredients, lower]
  );

  const showCreate = trimmed.length >= 2 && !exactMatch && focused;

  // Reset highlight when filtered list changes
  useEffect(() => { setHighlightIdx(-1); }, [filtered.length]);

  // Click outside : ferme le menu
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selectIngredient = (it) => {
    onChange(it.name, it);
    setFocused(false);
    inputRef.current?.blur();
  };

  const createInline = async () => {
    if (creating || !trimmed) return;
    setCreating(true);
    try {
      const { data } = await api.post("/ingredients", { name: trimmed });
      toast.success(`Ingrédient « ${data.name} » créé`);
      onChange(data.name, data);
      if (onCreate) onCreate(data);
      setFocused(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const onKeyDown = (e) => {
    if (!focused) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const max = filtered.length + (showCreate ? 1 : 0);
      setHighlightIdx((i) => (i + 1) % Math.max(1, max));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const max = filtered.length + (showCreate ? 1 : 0);
      setHighlightIdx((i) => (i - 1 + max) % Math.max(1, max));
    } else if (e.key === "Enter") {
      if (highlightIdx >= 0 && highlightIdx < filtered.length) {
        e.preventDefault();
        selectIngredient(filtered[highlightIdx]);
      } else if (showCreate && (highlightIdx === filtered.length || highlightIdx === -1)) {
        e.preventDefault();
        createInline();
      }
    } else if (e.key === "Escape") {
      setFocused(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Input
        ref={inputRef}
        value={value || ""}
        onChange={(e) => onChange(e.target.value, null)}
        onFocus={() => setFocused(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="h-8 text-sm"
        data-testid={testid}
        autoComplete="off"
      />
      {focused && (filtered.length > 0 || showCreate) && (
        <div className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-[#E5E5E0] rounded shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-menu`}>
          {filtered.map((it, idx) => (
            <button
              key={it.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); selectIngredient(it); }}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={`w-full text-left px-2.5 py-1.5 text-xs border-b border-[#F0F0EB] last:border-b-0 ${highlightIdx === idx ? "bg-[#F4FAF5]" : "hover:bg-[#FAFAF7]"}`}
            >
              <div className="font-medium text-[#1A1C1A]">{it.name}</div>
              <div className="text-[10px] text-[#575A57] flex items-center gap-2">
                <span>{it.category}</span>
                {it.default_price_chf != null && (
                  <span className="font-mono">· {Number(it.default_price_chf).toFixed(2)} CHF/{it.default_price_unit || "kg"}</span>
                )}
                {(it.allergens || []).length > 0 && (
                  <span className="text-[#C05C47]">· {it.allergens.join(", ")}</span>
                )}
              </div>
            </button>
          ))}
          {showCreate && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); createInline(); }}
              onMouseEnter={() => setHighlightIdx(filtered.length)}
              disabled={creating}
              className={`w-full text-left px-2.5 py-1.5 text-xs border-t border-[#E5E5E0] flex items-center gap-1.5 ${highlightIdx === filtered.length ? "bg-[#E8EEE9]" : "hover:bg-[#F4FAF5]"} text-[#1F4E3D] font-medium`}
              data-testid={`${testid}-create`}
            >
              <Plus className="w-3 h-3" />
              {creating ? "Création…" : <>Créer « <span className="italic">{trimmed}</span> » dans la BD</>}
              <Sparkles className="w-3 h-3 ml-auto opacity-60" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
